/**
 * Recipe URL Scraper
 * Kept as-is from original - well tested, works correctly
 */

import { normalizeUnit } from './units.js';

export async function importRecipeFromUrl(url) {
  try {
    const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(url)}`;
    const response = await fetch(proxyUrl);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const html = await response.text();
    const recipe = extractRecipeData(html, url);

    if (!recipe) {
      throw new Error('Could not extract recipe data from this URL');
    }

    return recipe;
  } catch (error) {
    console.error('Error fetching recipe:', error);
    throw new Error('Failed to import recipe. The site may be blocking access or the URL may be invalid.');
  }
}

function extractRecipeData(html, url) {
  const jsonLdRecipe = extractJsonLd(html);
  if (jsonLdRecipe) return jsonLdRecipe;
  return extractFromHtml(html, url);
}

function extractJsonLd(html) {
  try {
    const jsonLdMatches = html.match(/<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi);
    if (!jsonLdMatches) return null;

    for (const match of jsonLdMatches) {
      const jsonText = match.replace(/<script[^>]*>/, '').replace(/<\/script>/, '');
      const data = JSON.parse(jsonText);
      const recipes = Array.isArray(data) ? data : [data];

      for (const item of recipes) {
        let recipe = null;
        if (item['@type'] === 'Recipe') {
          recipe = item;
        } else if (item['@graph']) {
          recipe = item['@graph'].find((g) => g['@type'] === 'Recipe');
        }
        if (recipe) return parseJsonLdRecipe(recipe);
      }
    }
    return null;
  } catch (error) {
    console.error('Error parsing JSON-LD:', error);
    return null;
  }
}

/**
 * Flatten recipeInstructions into a plain array of step text strings.
 * Handles: string[], HowToStep[], HowToSection[] (with nested itemListElement),
 * and mixed arrays (e.g. NYT Cooking uses HowToSection wrapping HowToSteps).
 */
function flattenInstructionSteps(instructions) {
  const steps = [];
  for (const item of instructions) {
    if (typeof item === 'string') {
      steps.push(item);
    } else if (item['@type'] === 'HowToSection' && item.itemListElement) {
      const elements = Array.isArray(item.itemListElement) ? item.itemListElement : [item.itemListElement];
      for (const step of elements) {
        const text = step.text || step.name;
        if (text) steps.push(text);
      }
    } else {
      const text = item.text || item.name;
      if (text) steps.push(text);
    }
  }
  return steps;
}

function parseJsonLdRecipe(recipe) {
  const name = recipe.name || 'Untitled Recipe';

  let cookTime = 0;
  const totalTime = recipe.totalTime || recipe.cookTime || recipe.prepTime;
  if (totalTime) cookTime = parseDuration(totalTime);

  let servings = 4;
  if (recipe.recipeYield) {
    const yieldMatch = String(recipe.recipeYield).match(/\d+/);
    if (yieldMatch) servings = parseInt(yieldMatch[0]);
  }

  let instructions = '';
  if (recipe.recipeInstructions) {
    if (typeof recipe.recipeInstructions === 'string') {
      instructions = recipe.recipeInstructions;
    } else if (Array.isArray(recipe.recipeInstructions)) {
      const steps = flattenInstructionSteps(recipe.recipeInstructions);
      instructions = steps.map((text, i) => `${i + 1}. ${text}`).join('\n\n');
    }
  }

  const ingredients = [];
  if (recipe.recipeIngredient && Array.isArray(recipe.recipeIngredient)) {
    recipe.recipeIngredient.forEach((ing) => {
      const parsed = parseIngredient(ing);
      if (parsed) ingredients.push(parsed);
    });
  }

  return {
    name,
    cook_time_mins: cookTime,
    servings,
    instructions,
    ingredients,
    source: recipe.author?.name || 'Imported from URL',
  };
}

function parseDuration(duration) {
  if (!duration) return 0;
  const matches = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!matches) return 0;
  return (parseInt(matches[1] || 0)) * 60 + parseInt(matches[2] || 0);
}

function parseIngredient(ingredientStr) {
  let str = ingredientStr.trim();

  const knownUnits = [
    'tsp', 'teaspoon', 'teaspoons', 'tbsp', 'tablespoon', 'tablespoons',
    'cup', 'cups', 'c', 'oz', 'ounce', 'ounces', 'lb', 'lbs', 'pound', 'pounds',
    'g', 'gram', 'grams', 'kg', 'kilogram', 'kilograms',
    'ml', 'milliliter', 'milliliters', 'l', 'liter', 'liters',
    'pt', 'pint', 'pints', 'qt', 'quart', 'quarts', 'gal', 'gallon', 'gallons',
    'fl oz', 'fluid ounce', 'fluid ounces',
    'clove', 'cloves', 'piece', 'pieces', 'can', 'cans',
    'jar', 'jars', 'package', 'packages', 'bunch', 'bunches', 'whole',
  ];

  // Handle articles: "a package of chicken" / "an onion" → quantity 1
  let quantity = '';
  if (/^(a|an|one)\s+/i.test(str)) {
    quantity = '1';
    str = str.replace(/^(a|an|one)\s+/i, '');
  }

  const quantityMatch = str.match(/^([\d½¼¾⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞\/\.\s]+)/);
  let remainder = str;

  if (quantityMatch && !quantity) {
    quantity = quantityMatch[1].trim();
    remainder = str.slice(quantityMatch[0].length).trim();
  }

  let unit = '';
  let item = remainder;
  const words = remainder.split(/\s+/);

  for (let i = 0; i < Math.min(3, words.length); i++) {
    const potentialUnit = words.slice(0, i + 1).join(' ').toLowerCase();
    if (knownUnits.includes(potentialUnit)) {
      unit = words.slice(0, i + 1).join(' ');
      item = words.slice(i + 1).join(' ');
      break;
    }
  }

  // Normalize unit to its canonical form (tablespoons → tbsp, pounds → lb, etc.)
  unit = normalizeUnit(unit);

  item = item.trim().toLowerCase();
  // Strip "of" connector word ("2 cups of flour" → "flour")
  item = item.replace(/^of\s+/, '');
  item = item.replace(/\s*\([^)]*\)/g, '');
  item = item.replace(/,\s*(diced|chopped|sliced|minced|grated|shredded|as needed|to taste|optional).*$/i, '');
  // Strip leading size/quality adjectives, handling stacked ones ("boneless skinless chicken")
  const leadingDescriptors = /^(large|small|medium|extra-large|fresh|dried|boneless|skinless)\s+/i;
  let prev;
  do {
    prev = item;
    item = item.replace(leadingDescriptors, '');
  } while (item !== prev);

  if (!item) item = ingredientStr.trim().toLowerCase();

  return { item, quantity, unit, category: guessCategory(item) };
}

function guessCategory(item) {
  const lowerItem = item.toLowerCase();
  if (/vegetable|lettuce|tomato|onion|garlic|potato|carrot|celery|pepper|spinach|kale|cucumber|zucchini|squash|broccoli|cauliflower|cabbage|mushroom|corn|peas|beans|fruit|apple|banana|orange|lemon|lime|berry|melon/.test(lowerItem)) return 'produce';
  if (/chicken|beef|pork|turkey|fish|salmon|tuna|shrimp|meat|steak|bacon|sausage|ham|egg/.test(lowerItem)) return 'protein';
  if (/milk|cream|cheese|butter|yogurt|sour cream/.test(lowerItem)) return 'dairy';
  if (/frozen/.test(lowerItem)) return 'frozen';
  if (/bread|bun|roll|tortilla|pita/.test(lowerItem)) return 'bakery';
  return 'pantry';
}

function extractFromHtml(html, url) {
  const titleMatch = html.match(/<title[^>]*>([^<]+)<\/title>/i);
  const name = titleMatch ? titleMatch[1].replace(/\s*[-|]\s*.+$/, '').trim() : 'Imported Recipe';

  return {
    name,
    cook_time_mins: 30,
    servings: 4,
    instructions: 'Please manually enter instructions from the source website.',
    ingredients: [],
    source: url,
  };
}
